import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent } from "react";
import { Button, Dropdown, Menu } from "@arco-design/web-react";
import {
  ArrowLeft,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CircleX,
  Maximize2,
  Minimize2,
  RefreshCw,
  X,
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";

import { takeAdminSoloTagsFlag, ADMIN_SOLO_TAGS_QUERY_KEY } from "../../lib/adminSoloTags";
import {
  type AdminTagItem,
  ADMIN_TAGS_HOME_PATH,
  buildAdminTagHref,
  closeAdminTag,
  closeAllAdminTags,
  closeLeftAdminTags,
  closeOtherAdminTags,
  normalizeAdminTagPathname,
  readAdminTagsFromStorage,
  shouldTrackAdminTag,
  upsertAdminTag,
  writeAdminTagsToStorage,
} from "./adminTagsViewState";
import { resolveAdminNavigationIcon } from "./admin.config";
import "./AdminTagsView.css";

type AdminTagsViewProps = {
  pageTitle: string;
  onRefresh: () => void;
  /** 是否处于整站沉浸（隐藏侧栏/顶栏，页签列表收起）。 */
  immersive?: boolean;
  /** 切换整站沉浸；未传入时不展示全屏按钮。 */
  onToggleImmersive?: () => void;
};

type ContextAction = "refresh" | "close-current" | "close-others" | "close-left" | "close-all";

/** 读取当前页真实 query（兼容业务页用 replaceState 写 URL、React Router 未同步的情况）。 */
function readLiveSearch(pathname: string, fallbackSearch: string): string | undefined {
  if (typeof window === "undefined") {
    return fallbackSearch || undefined;
  }
  if (normalizeAdminTagPathname(window.location.pathname) === pathname) {
    return window.location.search || undefined;
  }
  return fallbackSearch || undefined;
}

/**
 * 若依风格后台多标签页：访问路由自动开页签，支持切换、右键菜单与刷新。
 * 标签按 pathname 去重；search 单独保存，切换时恢复筛选条件。
 */
export function AdminTagsView({
  pageTitle,
  onRefresh,
  immersive = false,
  onToggleImmersive,
}: AdminTagsViewProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const activeRoutePath = useMemo(
    () => normalizeAdminTagPathname(location.pathname),
    [location.pathname],
  );
  const resolvedTagTitle =
    pageTitle === "工作台" &&
    (/^\/crm\/customization\/projects\/[^/]+\/workbench/.test(location.pathname) ||
      /^\/crm\/opportunities\/[^/]+\/customization-projects\/[^/]+\/workbench/.test(
        location.pathname,
      ))
      ? "定制工作台"
      : pageTitle;
  const [tags, setTags] = useState<AdminTagItem[]>(() => readAdminTagsFromStorage());
  const scrollRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef(resolvedTagTitle);
  titleRef.current = resolvedTagTitle;
  const activeRoutePathRef = useRef(activeRoutePath);
  activeRoutePathRef.current = activeRoutePath;
  const locationSearchRef = useRef(location.search);
  locationSearchRef.current = location.search;

  const persistCurrentSearch = useCallback((previous: AdminTagItem[]): AdminTagItem[] => {
    const liveSearch = readLiveSearch(activeRoutePathRef.current, locationSearchRef.current);
    const existing = previous.find((tag) => tag.path === activeRoutePathRef.current);
    return upsertAdminTag(previous, {
      path: activeRoutePathRef.current,
      title: titleRef.current,
      search: liveSearch ?? existing?.search,
    });
  }, []);

  useEffect(() => {
    const liveSearch = readLiveSearch(activeRoutePath, location.search);
    const { solo, search } = takeAdminSoloTagsFlag(liveSearch);
    setTags((previous) => {
      let next = upsertAdminTag(previous, {
        path: activeRoutePath,
        title: resolvedTagTitle,
        search,
      });
      if (solo) {
        next = closeOtherAdminTags(next, activeRoutePath);
      }
      writeAdminTagsToStorage(next);
      return next;
    });
    if (solo && location.search.includes(ADMIN_SOLO_TAGS_QUERY_KEY)) {
      navigate(
        { pathname: location.pathname, search: search ?? "" },
        { replace: true },
      );
    }
  }, [activeRoutePath, location.pathname, location.search, navigate, resolvedTagTitle]);

  // 业务页若用 history.replaceState 写筛选，同步回当前标签的 search。
  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }
    const historyRef = window.history;
    const originalReplaceState = historyRef.replaceState.bind(historyRef);
    historyRef.replaceState = ((data: unknown, unused: string, url?: string | URL | null) => {
      originalReplaceState(data, unused, url as string | URL | null | undefined);
      const href =
        typeof url === "string"
          ? url
          : url instanceof URL
            ? `${url.pathname}${url.search}`
            : `${window.location.pathname}${window.location.search}`;
      const resolved = new URL(href, window.location.origin);
      const path = normalizeAdminTagPathname(resolved.pathname);
      if (path !== activeRoutePathRef.current || !shouldTrackAdminTag(path)) {
        return;
      }
      const search = resolved.search || undefined;
      setTags((previous) => {
        const current = previous.find((tag) => tag.path === path);
        if (!current || (current.search ?? "") === (search ?? "")) {
          return previous;
        }
        const next = upsertAdminTag(previous, {
          path,
          title: titleRef.current || current.title,
          search,
        });
        writeAdminTagsToStorage(next);
        return next;
      });
    }) as History["replaceState"];

    return () => {
      historyRef.replaceState = originalReplaceState;
    };
  }, []);

  const scrollBy = useCallback((offset: number) => {
    scrollRef.current?.scrollBy({ left: offset, behavior: "smooth" });
  }, []);

  const handleSelect = useCallback(
    (tag: AdminTagItem) => {
      if (tag.path === activeRoutePath) {
        return;
      }
      setTags((previous) => {
        const next = persistCurrentSearch(previous);
        writeAdminTagsToStorage(next);
        return next;
      });
      navigate(buildAdminTagHref(tag));
    },
    [activeRoutePath, navigate, persistCurrentSearch],
  );

  const applyTags = useCallback(
    (result: { tags: AdminTagItem[]; nextActivePath: string }) => {
      writeAdminTagsToStorage(result.tags);
      const nextPath = normalizeAdminTagPathname(result.nextActivePath.split("?")[0] ?? result.nextActivePath);
      if (nextPath !== activeRoutePath) {
        const targetTag = result.tags.find((tag) => tag.path === nextPath);
        navigate(targetTag ? buildAdminTagHref(targetTag) : result.nextActivePath);
      }
      return result.tags;
    },
    [activeRoutePath, navigate],
  );

  const handleClose = useCallback(
    (event: MouseEvent, path: string) => {
      event.stopPropagation();
      setTags((previous) => {
        const withSearch = persistCurrentSearch(previous);
        return applyTags(closeAdminTag(withSearch, path, activeRoutePath));
      });
    },
    [activeRoutePath, applyTags, persistCurrentSearch],
  );

  const handleCloseOthers = useCallback(
    (targetPath: string) => {
      setTags((previous) => {
        const withSearch = persistCurrentSearch(previous);
        const next = closeOtherAdminTags(withSearch, targetPath);
        writeAdminTagsToStorage(next);
        if (activeRoutePath !== targetPath && activeRoutePath !== ADMIN_TAGS_HOME_PATH) {
          const target = next.find((tag) => tag.path === targetPath);
          navigate(target ? buildAdminTagHref(target) : targetPath);
        }
        return next;
      });
    },
    [activeRoutePath, navigate, persistCurrentSearch],
  );

  const handleCloseLeft = useCallback(
    (targetPath: string) => {
      setTags((previous) => {
        const withSearch = persistCurrentSearch(previous);
        return applyTags(closeLeftAdminTags(withSearch, targetPath, activeRoutePath));
      });
    },
    [activeRoutePath, applyTags, persistCurrentSearch],
  );

  const handleCloseAll = useCallback(() => {
    setTags((previous) => {
      const next = closeAllAdminTags(previous);
      writeAdminTagsToStorage(next);
      if (activeRoutePath !== next[0]?.path) {
        navigate(next[0]?.path ?? ADMIN_TAGS_HOME_PATH);
      }
      return next;
    });
  }, [activeRoutePath, navigate]);

  const handleRefreshTarget = useCallback(
    (target: AdminTagItem) => {
      const href = buildAdminTagHref(target);
      if (target.path !== activeRoutePath) {
        setTags((previous) => {
          const next = persistCurrentSearch(previous);
          writeAdminTagsToStorage(next);
          return next;
        });
        navigate(href);
      }
      // 下一帧刷新，确保先切到目标页再重挂载 Outlet。
      window.setTimeout(() => onRefresh(), 0);
    },
    [activeRoutePath, navigate, onRefresh, persistCurrentSearch],
  );

  const handleContextAction = useCallback(
    (action: ContextAction, target: AdminTagItem) => {
      if (action === "refresh") {
        handleRefreshTarget(target);
        return;
      }
      if (action === "close-current") {
        if (target.affix || target.path === ADMIN_TAGS_HOME_PATH) {
          return;
        }
        setTags((previous) => {
          const withSearch = persistCurrentSearch(previous);
          return applyTags(closeAdminTag(withSearch, target.path, activeRoutePath));
        });
        return;
      }
      if (action === "close-others") {
        handleCloseOthers(target.path);
        return;
      }
      if (action === "close-left") {
        handleCloseLeft(target.path);
        return;
      }
      handleCloseAll();
    },
    [
      activeRoutePath,
      applyTags,
      handleCloseAll,
      handleCloseLeft,
      handleCloseOthers,
      handleRefreshTarget,
      persistCurrentSearch,
    ],
  );

  const renderContextMenu = useCallback(
    (target: AdminTagItem) => {
      const targetIndex = tags.findIndex((tag) => tag.path === target.path);
      const hasClosableLeft = tags
        .slice(0, Math.max(targetIndex, 0))
        .some((tag) => !tag.affix && tag.path !== ADMIN_TAGS_HOME_PATH);
      const canCloseCurrent = !target.affix && target.path !== ADMIN_TAGS_HOME_PATH;

      return (
        <Menu
          className="admin-tags-view__context-menu"
          data-testid="admin-tags-context-menu"
          onClickMenuItem={(key) => handleContextAction(key as ContextAction, target)}
        >
          <Menu.Item key="refresh">
            <span className="admin-tags-view__menu-item">
              <RefreshCw size={14} />
              刷新页面
            </span>
          </Menu.Item>
          <Menu.Item key="close-current" disabled={!canCloseCurrent}>
            <span className="admin-tags-view__menu-item">
              <X size={14} />
              关闭当前
            </span>
          </Menu.Item>
          <Menu.Item key="close-others">
            <span className="admin-tags-view__menu-item">
              <CircleX size={14} />
              关闭其他
            </span>
          </Menu.Item>
          <Menu.Item key="close-left" disabled={!hasClosableLeft}>
            <span className="admin-tags-view__menu-item">
              <ArrowLeft size={14} />
              关闭左侧
            </span>
          </Menu.Item>
          <Menu.Item key="close-all">
            <span className="admin-tags-view__menu-item">
              <CircleX size={14} />
              全部关闭
            </span>
          </Menu.Item>
        </Menu>
      );
    },
    [handleContextAction, tags],
  );

  return (
    <div
      className={`admin-tags-view${immersive ? " is-immersive" : ""}`}
      data-testid="admin-tags-view"
      data-immersive={immersive ? "true" : "false"}
      role="navigation"
      aria-label="已打开页面标签"
    >
      {immersive ? null : (
        <Button
          type="text"
          size="mini"
          className="admin-tags-view__scroll-btn"
          aria-label="向左滚动标签"
          icon={<ChevronLeft size={16} />}
          onClick={() => scrollBy(-180)}
        />
      )}
      {immersive ? null : (
      <div className="admin-tags-view__scroll" ref={scrollRef} role="tablist" aria-label="页面标签列表">
        {tags.map((tag) => {
          const selected = tag.path === activeRoutePath;
          return (
            <Dropdown key={tag.path} trigger="contextMenu" position="bl" droplist={renderContextMenu(tag)}>
              <button
                type="button"
                role="tab"
                aria-label={tag.title}
                title={tag.title}
                aria-selected={selected}
                className={`admin-tags-view__tab${selected ? " is-active" : ""}`}
                onClick={() => handleSelect(tag)}
              >
                <span className="admin-tags-view__tab-icon" aria-hidden="true">
                  {resolveAdminNavigationIcon(tag.path)}
                </span>
                <span className="admin-tags-view__tab-title">{tag.title}</span>
                {tag.affix ? null : (
                  <span
                    className="admin-tags-view__tab-close"
                    role="button"
                    tabIndex={0}
                    aria-label={`关闭 ${tag.title}`}
                    onClick={(event) => handleClose(event, tag.path)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        handleClose(event as unknown as MouseEvent, tag.path);
                      }
                    }}
                  >
                    <X size={12} />
                  </span>
                )}
              </button>
            </Dropdown>
          );
        })}
      </div>
      )}
      <div className="admin-tags-view__actions">
        {immersive ? null : (
          <>
            <Button
              type="text"
              size="mini"
              className="admin-tags-view__scroll-btn"
              aria-label="向右滚动标签"
              icon={<ChevronRight size={16} />}
              onClick={() => scrollBy(180)}
            />
            <Dropdown
              trigger="click"
              position="br"
              droplist={(
                <Menu
                  onClickMenuItem={(key) => {
                    const activeTag = tags.find((tag) => tag.path === activeRoutePath) ?? tags[0];
                    if (!activeTag) {
                      return;
                    }
                    handleContextAction(key as ContextAction, activeTag);
                  }}
                >
                  <Menu.Item key="refresh">刷新页面</Menu.Item>
                  <Menu.Item key="close-current">关闭当前</Menu.Item>
                  <Menu.Item key="close-others">关闭其他</Menu.Item>
                  <Menu.Item key="close-left">关闭左侧</Menu.Item>
                  <Menu.Item key="close-all">全部关闭</Menu.Item>
                </Menu>
              )}
            >
              <Button
                type="text"
                size="mini"
                className="admin-tags-view__scroll-btn"
                aria-label="标签页更多操作"
                icon={<ChevronDown size={16} />}
              />
            </Dropdown>
          </>
        )}
        <Button
          type="text"
          size="mini"
          className="admin-tags-view__refresh"
          aria-label="刷新当前页"
          icon={<RefreshCw size={14} />}
          onClick={onRefresh}
        >
          刷新
        </Button>
        {onToggleImmersive ? (
          <Button
            type="text"
            size="mini"
            className="admin-tags-view__immersive"
            aria-label={immersive ? "退出全屏" : "进入全屏"}
            aria-pressed={immersive}
            icon={immersive ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
            onClick={onToggleImmersive}
          >
            {immersive ? "退出全屏" : "全屏"}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
